<template>
  <AppLayout active-item="tasks">
    <AePageHeader title="Celery Admin" />
    <AePageContent>

      <!-- ── Lancia task ─────────────────────────────────────────────── -->
      <AeCard class="ca-section">
        <template #header><h3>Lancia task</h3></template>

        <div class="ca-form-row">
          <div class="ca-field">
            <label>Modello</label>
            <select v-model="selectedModel" class="ca-select">
              <option value="" disabled>— seleziona —</option>
              <option
                v-for="m in models"
                :key="m.task_name"
                :value="m"
                :disabled="!m.is_active"
              >
                {{ m.label }}{{ !m.is_active ? ' (inattivo)' : '' }}
              </option>
            </select>
          </div>

          <div v-if="selectedModel" class="ca-field ca-field--desc">
            <span class="ca-model-desc">{{ selectedModel.description }}</span>
            <span class="ca-model-meta">{{ selectedModel.task_name }} · coda: {{ selectedModel.queue }}</span>
          </div>
        </div>

        <div class="ca-field">
          <label>Payload JSON</label>
          <textarea
            v-model="payloadText"
            class="ca-textarea"
            rows="4"
            placeholder='{ "seconds": 5 }'
            :class="{ 'ca-textarea--error': payloadError }"
          />
          <span v-if="payloadError" class="ca-field-error">{{ payloadError }}</span>
        </div>

        <div class="ca-form-actions">
          <AeButton variant="primary" :disabled="!selectedModel || !!payloadError || task.running.value" @click="sendTask">
            Invia
          </AeButton>
          <AeButton variant="ghost" @click="resetForm">Reset</AeButton>
        </div>

        <!-- Call log -->
        <div v-if="task.callLog.value.length" class="ca-call-log">
          <div
            v-for="(entry, i) in task.callLog.value"
            :key="i"
            :class="['ca-log-entry', entry.ok ? 'ca-log-ok' : 'ca-log-err']"
          >
            <span class="ca-log-label">{{ entry.label }}</span>
            <span class="ca-log-msg">{{ entry.msg }}</span>
          </div>
        </div>

        <!-- Task attivo -->
        <div v-if="task.taskData.value" class="ca-task-status">
          <div class="ca-task-id">ID: {{ task.taskData.value.id_task }}</div>
          <div class="ca-status-row">
            <span :class="['ca-badge', `ca-badge-${task.taskData.value.status?.toLowerCase()}`]">
              {{ task.taskData.value.status }}
            </span>
            <span class="ca-progress-text">{{ task.taskData.value.progress ?? 0 }}%</span>
          </div>
          <div class="ca-progress-bar">
            <div class="ca-progress-fill" :style="{ width: (task.taskData.value.progress ?? 0) + '%' }" />
          </div>
          <div v-if="task.taskData.value.status === 'SUCCESS'" class="ca-result">
            ✓ {{ JSON.stringify(task.taskData.value.result) }}
          </div>
          <div v-if="task.taskData.value.status === 'FAILURE'" class="ca-error">
            ✗ {{ task.taskData.value.error }}
          </div>
        </div>
      </AeCard>

      <!-- ── Ultimi task ──────────────────────────────────────────────── -->
      <AeCard class="ca-section">
        <template #header>
          <h3>Ultimi 50 task</h3>
          <AeButton variant="ghost" size="sm" @click="loadTasks">↻ Aggiorna</AeButton>
        </template>

        <div v-if="loadingTasks" class="ca-loading">Caricamento…</div>
        <table v-else-if="recentTasks.length" class="ca-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Coda</th>
              <th>Stato</th>
              <th>%</th>
              <th>Lanciato da</th>
              <th>Creato</th>
              <th>Durata</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="t in recentTasks"
              :key="t.id_task"
              :class="{ 'ca-row-active': t.id_task === task.taskData.value?.id_task }"
            >
              <td class="ca-mono">{{ t.task_name }}</td>
              <td>{{ t.queue }}</td>
              <td><span :class="['ca-badge', `ca-badge-${t.status?.toLowerCase()}`]">{{ t.status }}</span></td>
              <td>{{ t.progress ?? 0 }}%</td>
              <td>{{ t.launched_by }}</td>
              <td>{{ formatDate(t.created_at) }}</td>
              <td>{{ formatDuration(t.started_at, t.completed_at) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="ca-empty">Nessun task</div>
      </AeCard>

    </AePageContent>
  </AppLayout>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue'
import AppLayout from '../layouts/AppLayout.vue'
import { AePageHeader, AePageContent, AeCard, AeButton } from '@aevue/components'
import { api } from '../services/api.js'
import { useCeleryTask } from '../composables/useCeleryTask.js'

// ── Modelli ────────────────────────────────────────────────────────────────
const models        = ref([])
const selectedModel = ref(null)
const payloadText   = ref('{}')
const payloadError  = ref('')

watch(selectedModel, (m) => {
  if (!m) return
  payloadText.value = m.task_name === 'example_task'
    ? '{ "seconds": 5, "message": "hello" }'
    : '{}'
})

watch(payloadText, (v) => {
  try { JSON.parse(v); payloadError.value = '' }
  catch { payloadError.value = 'JSON non valido' }
})

async function loadModels() {
  const res = await api.get('/api/v2/tasks/models')
  if (res.ok) models.value = res.data || []
}

// ── Task ───────────────────────────────────────────────────────────────────
const recentTasks  = ref([])
const loadingTasks = ref(false)

const task = useCeleryTask({ onDone: () => loadTasks() })

async function sendTask() {
  if (!selectedModel.value || payloadError.value || task.running.value) return
  let payload
  try { payload = JSON.parse(payloadText.value) } catch { return }

  task.reset()
  const res = await api.post('/api/v2/tasks/enqueue', {
    task_name: selectedModel.value.task_name,
    payload,
  })
  if (!res.ok) {
    task.callLog.value.unshift({ label: 'ENQUEUE', ok: false, msg: res.message || 'errore' })
    return
  }
  task.callLog.value.unshift({ label: 'ENQUEUE', ok: true, msg: `id_task: ${res.id_task}` })
  task.start(res.id_task)
}

function resetForm() {
  task.reset()
  selectedModel.value = null
  payloadText.value = '{}'
}

async function loadTasks() {
  loadingTasks.value = true
  const res = await api.get('/api/v2/tasks/tasks')
  if (res.ok) recentTasks.value = res.data || []
  loadingTasks.value = false
}

function formatDate(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'medium' })
}

function formatDuration(s, e) {
  if (!s) return '—'
  const sec = Math.round((new Date(e || Date.now()) - new Date(s)) / 1000)
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`
}

loadModels()
loadTasks()
</script>

<style scoped>
.ca-section { margin-bottom: 1.5rem; }

.ca-form-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  align-items: flex-start;
}
.ca-field { display: flex; flex-direction: column; gap: 0.3rem; }
.ca-field label { font-size: 0.85rem; font-weight: 600; color: #555; }
.ca-field--desc { flex: 1; justify-content: center; padding-top: 1.5rem; }
.ca-model-desc { font-size: 0.9rem; color: #333; }
.ca-model-meta { font-size: 0.78rem; color: #888; font-family: monospace; }

.ca-select {
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--ae-border-color, #ccc);
  border-radius: 4px;
  font-size: 0.95rem;
  min-width: 240px;
}
.ca-textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--ae-border-color, #ccc);
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.85rem;
  resize: vertical;
}
.ca-textarea--error { border-color: #dc2626; }
.ca-field-error { font-size: 0.8rem; color: #dc2626; }

.ca-form-actions { display: flex; gap: 0.5rem; margin-top: 0.75rem; }

.ca-task-status { margin-top: 1rem; }
.ca-task-id { font-size: 0.78rem; color: #888; margin-bottom: 0.5rem; font-family: monospace; }
.ca-status-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; }
.ca-progress-text { font-size: 0.9rem; color: #666; }
.ca-progress-bar {
  height: 6px;
  background: var(--ae-border-color, #e0e0e0);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}
.ca-progress-fill {
  height: 100%;
  background: var(--ae-primary, #2563eb);
  transition: width 0.4s ease;
}
.ca-result { color: #16a34a; font-size: 0.9rem; margin-top: 0.5rem; }
.ca-error  { color: #dc2626; font-size: 0.9rem; margin-top: 0.5rem; }

.ca-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
}
.ca-badge-pending  { background: #fef9c3; color: #854d0e; }
.ca-badge-started  { background: #dbeafe; color: #1e40af; }
.ca-badge-progress { background: #dbeafe; color: #1e40af; }
.ca-badge-success  { background: #dcfce7; color: #166534; }
.ca-badge-failure  { background: #fee2e2; color: #991b1b; }
.ca-badge-revoked  { background: #f3f4f6; color: #4b5563; }

.ca-call-log {
  margin-top: 0.75rem;
  font-family: monospace;
  font-size: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ca-log-entry { display: flex; gap: 0.5rem; padding: 0.2rem 0.4rem; border-radius: 3px; }
.ca-log-ok  { background: #f0fdf4; color: #166534; }
.ca-log-err { background: #fef2f2; color: #991b1b; }
.ca-log-label { font-weight: 700; min-width: 90px; }
.ca-log-msg { word-break: break-all; }

.ca-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.ca-table th {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid var(--ae-border-color, #e0e0e0);
  color: #666;
  font-weight: 600;
}
.ca-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--ae-border-color, #f0f0f0);
}
.ca-row-active td { background: #eff6ff; }
.ca-mono { font-family: monospace; font-size: 0.82rem; }
.ca-loading, .ca-empty { color: #888; font-size: 0.9rem; padding: 0.5rem 0; }
</style>
